import React, { useState } from "react";
import {
  Image,
  Text,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  List,
  ListItem,
  Button,
  IconButton,
  Flex,
} from "@chakra-ui/react";
import { Timeline } from "../Timeline/Timeline";
import { COLORS } from "../../constants/COLORS";
import { ArrowBackIcon, ArrowForwardIcon } from "@chakra-ui/icons";
import { motion } from "framer-motion";

export function TimelineDemo() {
  const textColor = useColorModeValue(COLORS.DARK_BLUE, COLORS.WHITE_SMOKE);
  const backgroundColor = useColorModeValue(COLORS.WHITE_SMOKE, "transparent");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const handleImageClick = (images: string[]) => {
    setSelectedImages(images);
    setCurrentImageIndex(0);
    onOpen();
  };

  const handleNextImage = () => {
    if (currentImageIndex < selectedImages.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    }
  };

  const handlePrevImage = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };

  const data = [
    {
      title: "2026",
      content: (
        <div>
          <Text mb={4} color={textColor}>
            This period represented an important transition in my professional
            and academic journey. Alongside my Computer Engineering studies,
            where I continued developing my knowledge of software development,
            algorithms, data structures, object-oriented programming, C, C++,
            C#, and .NET, I concluded a significant chapter of my career.
            <br />
            <br /> In June, after nearly four years at Manutan, I closed a
            journey that took me from Frontend Developer to Webmaster and the
            CRO team. During this time, I combined frontend development,
            analytics, and experimentation, contributing to more than 100 A/B
            tests across large-scale B2B e-commerce platforms.<br />
            <br />
            In July, I joined Incubeta as an Analytics Technical Specialist,
            beginning a new chapter focused on advanced tracking, digital
            analytics, data quality, and measurement.
          </Text>
        </div>
      ),
    },
    {
      title: "2025",
      content: (
        <div>
          <Text mb={4} color={textColor}>
            Began a year of significant professional growth through freelance
            collaborations focused on advanced digital analytics, tracking, and
            data quality initiatives.
            <br />
            <br />
            Started working with Dengun as a Frontend Tracking Specialist,
            contributing to GA4 implementations, event tracking strategies,
            server-side tagging, and consent management solutions.
            Responsibilities included diagnosing and resolving complex Google
            Tag Manager issues, implementing privacy-compliant consent
            frameworks, and delivering technical audits and reports to improve
            data accuracy, reliability, and measurement maturity across client
            environments.
            <br />
            <br />
            In parallel, expanded my international experience through a
            collaboration with Motion Source as an Analytics Implementation
            Engineer, supporting analytics and tracking initiatives across 11
            clients in the United States market. Led end-to-end implementation
            projects, designing scalable event architectures, ensuring data
            consistency, validating tracking accuracy, and maintaining
            compliance with evolving privacy regulations. Regularly worked with
            advanced GA4 and Google Tag Manager configurations, including
            server-side implementations and cross-platform measurement
            strategies.
            <br />
            <br />
            Within Manutan, I was promoted from Frontend Developer to Webmaster,
            reflecting an expanded scope of responsibilities within the Digital
            and CRO teams. In this role, I became responsible for the
            implementation of A/B testing initiatives, collaborating closely
            with UX, Marketing, Analytics, and CRO stakeholders to improve
            website performance and user experience. I was actively involved in
            the technical implementation of experiments, personalization
            campaigns, and website enhancements, contributing to data-driven
            optimisation initiatives aimed at improving conversion rates,
            customer engagement, and overall business performance.
            <br />
            <br />
            Alongside professional work, I enrolled in an evening BSc in
            Computer Engineering at ISTEC, strengthening my academic foundations
            in software engineering, computer systems, and technology while
            remaining fully active in the industry.
          </Text>
        </div>
      ),
    },
    {
      title: "2024",
      content: (
        <div>
          <Text mb={4} color={textColor}>
            Completed an in-depth mobile development course with a focus on
            React Native, Kotlin, Swift, and DevOps , complemented by a UX/UI
            workshop to enhance design capabilities.
          </Text>
          <List spacing={3}>
            <ListItem>
              <Button
                style={{
                  whiteSpace: "normal",
                  textAlign: "left",
                }}
                variant="link"
                onClick={() => {
                  window.dataLayer?.push({
                    event: "cta_click",
                    cta_name: "ver_certificado_mobile_development",
                  });

                  handleImageClick([
                    "/certificates/crossplatform-1.jpg",
                    "/certificates/crossplatform-2.jpg",
                  ]);
                }}
              >
                Mobile Development Certificate
              </Button>
            </ListItem>
            <ListItem>
              <Button
                variant="link"
                onClick={() => {
                  window.dataLayer?.push({
                    event: "cta_click",
                    cta_name: "ver_certificado_uxui",
                  });

                  handleImageClick([
                    "/certificates/uxui-1.jpg",
                    "/certificates/uxui-2.jpg",
                  ]);
                }}
                style={{
                  whiteSpace: "normal",
                  textAlign: "left",
                }}
              >
                UX/UI Workshop Certificate
              </Button>
            </ListItem>
          </List>
        </div>
      ),
    },
    {
      title: "2023",
      content: (
        <div>
          <Text mb={4} color={textColor}>
            Completed the Full Stack Web Development course at Ironhack,
            followed by the start of a new role as a Front-End Developer on the
            marketing team at Manutan. Responsibilities include developing new
            features, components, and landing pages, as well as performing
            functional testing and debugging to ensure a smooth user experience.
            Also involved in implementing and maintaining tracking using Google
            Tag Manager (GTM) and GA4 to support data-driven decision-making.
          </Text>
          <List spacing={3}>
            <ListItem>
              <Button
                style={{
                  whiteSpace: "normal",
                  textAlign: "left",
                }}
                variant="link"
                onClick={() => {
                  window.dataLayer?.push({
                    event: "cta_click",
                    cta_name: "ver_certificado_ironhack_full_stack",
                  });

                  handleImageClick(["/certificates/ironhack.jpg"]);
                }}
              >
                Full Stack Web Development Certificate
              </Button>
            </ListItem>
            <ListItem>
              <Button
                variant="link"
                onClick={() => {
                  window.dataLayer?.push({
                    event: "cta_click",
                    cta_name: "ver_certificado_typescript",
                  });

                  handleImageClick(["/certificates/typescript.jpg"]);
                }}
                style={{
                  whiteSpace: "normal",
                  textAlign: "left",
                }}
              >
                Advanced Typescript Certificate
              </Button>
            </ListItem>
          </List>
        </div>
      ),
    },
    {
      title: "2022",
      content: (
        <div>
          <Text mb={4} color={textColor}>
            Worked as a Tachograph Analyst, managing truck drivers’ driving
            times and ensuring compliance with legal rest regulations. This role
            involved analysing data to produce decision-support reports,
            sharpening attention to detail and time management skills. During
            this period, also started a Full Stack Web Development course at
            Ironhack.
          </Text>
          {/*  <List spacing={3}>
            <ListItem>
              <Button
                variant="link"
                onClick={() => handleImageClick(["#"])}
              ></Button>
            </ListItem>
          </List> */}
        </div>
      ),
    },
    {
      title: "...",
      content: (
        <div>
          <></>
        </div>
      ),
    },
    {
      title: "2013",
      content: (
        <div>
          <Text mb={4} color={textColor}>
            At the age of 15, school studies were complemented by a technical
            course in Visual Basic and Access 2010, offering a first contact
            with programming and database management. This early exposure to
            technology helped spark an interest in the field and laid the
            groundwork for a future path in software development.
          </Text>
          <List spacing={3}>
            <ListItem>
              <Button
                style={{
                  whiteSpace: "normal",
                  textAlign: "left",
                }}
                variant="link"
                onClick={() => {
                  window.dataLayer?.push({
                    event: "cta_click",
                    cta_name: "ver_certificado_vb",
                  });
                  handleImageClick(["/certificates/vb.jpeg"]);
                }}
              >
                Visual Basic, Access 2010, and Programming Logic Certificate
              </Button>
            </ListItem>
          </List>
        </div>
      ),
    },
  ];

  return (
    <div className="w-full">
      <Timeline data={data} />
      <Modal isOpen={isOpen} onClose={onClose} size="2xl" allowPinchZoom={true}>
        <ModalOverlay />
        <ModalContent
          backgroundColor={"transparent"}
          backdropFilter={"blur(2px)"}
        >
          <ModalCloseButton />
          <ModalBody>
            {selectedImages.length > 0 && (
              <Flex direction="column" alignItems="center" mt={10}>
                <Image
                  src={selectedImages[currentImageIndex]}
                  alt={`Certificate Page ${currentImageIndex + 1}`}
                  width="100%"
                  height="auto"
                  className="rounded-lg"
                />
                {selectedImages.length > 1 && (
                  <Flex mt={4} justifyContent="space-between" width="100%">
                    <IconButton
                      aria-label="Previous"
                      icon={<ArrowBackIcon />}
                      onClick={handlePrevImage}
                      isDisabled={currentImageIndex === 0}
                      color={textColor}
                      backgroundColor={backgroundColor}
                    />
                    <IconButton
                      aria-label="Next"
                      icon={<ArrowForwardIcon />}
                      onClick={handleNextImage}
                      isDisabled={
                        currentImageIndex === selectedImages.length - 1
                      }
                      backgroundColor={backgroundColor}
                      color={textColor}
                    />
                  </Flex>
                )}
              </Flex>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
}
